import React, { useMemo } from "react";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { buildGroupTree } from "@components/forms/GroupSelectField/buildGroupTree";
import {
    NO_GROUP_LABEL,
    NO_GROUP_VALUE,
} from "@components/forms/GroupSelectField/GroupSelectField.constants";
import {
    filterGroupsBySelectableType,
    resolveSelectableGroupType,
} from "@components/forms/GroupSelectField/selectableGroupType";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { BaseCellProps } from "./index";
import styles from "./DropdownCell.module.scss";
import { stopPropagation, stopPropagationExceptTab } from "./cellEditorEvents";

interface AccountGroupCellProps extends BaseCellProps<string> {
    groups: AccountGroup[];
}

export const AccountGroupCell: React.FC<AccountGroupCellProps> = ({
    value,
    row,
    groups,
    isEditing,
    finishEditing,
    enterEditModeKey,
}) => {
    const accountType =
        typeof row.accountType === "string" ? row.accountType : null;
    const selectedType = resolveSelectableGroupType(accountType);

    const treeItems = useMemo<Array<PickerItemData<string>>>(() => {
        const noGroupNode: PickerItemData<string> = {
            value: NO_GROUP_VALUE,
            label: NO_GROUP_LABEL,
        };
        if (!selectedType) {
            return [noGroupNode];
        }
        const groupsOfType = filterGroupsBySelectableType(groups, accountType);
        return [noGroupNode, ...buildGroupTree(groupsOfType)];
    }, [groups, accountType, selectedType]);

    const treeValue = value ?? NO_GROUP_VALUE;

    const label = useMemo(() => {
        if (value) {
            return groups.find((group) => group.id === value)?.name ?? "";
        }
        return "";
    }, [groups, value]);

    const display = (
        <div className={styles.dropdownDisplay}>
            {label && (
                <BreakableText
                    text={label}
                    showTooltip
                    className={styles.dropdownLabel}
                />
            )}
        </div>
    );

    if (isEditing) {
        return (
            <>
                <div
                    className={styles.treeSelectCellEditor}
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    onKeyDown={stopPropagationExceptTab}
                >
                    <Picker.Select
                        items={treeItems}
                        value={treeValue}
                        onValueChange={(next) => {
                            finishEditing(
                                next === NO_GROUP_VALUE ? null : next,
                            );
                        }}
                        onOpenChange={(open) => {
                            if (!open) {
                                finishEditing();
                            }
                        }}
                        defaultOpen
                        searchable
                        cellEditor={{ openingKey: enterEditModeKey }}
                        placeholder="Select a group"
                        defaultExpandAll
                        selectableParents
                        trigger={
                            <Picker.Button variant="primary" size="sm" block />
                        }
                    />
                </div>
                {display}
            </>
        );
    }

    return <div className={styles.dropdownCell}>{display}</div>;
};
